import React from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import moment from 'moment'
import { useCandidate } from './CandidateProvider'
import NewResumeModal from './NewResumeModal'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'
import { useFormatTime } from '~/hooks/useFormatTime'

const useDocumentsAndSettings = () => {
  const { candidate: { id: candidateId } = {} } = useCandidate()

  const queryPath = `/coach/dashboard_candidates/${candidateId}/settings_and_documents.json`

  const queryFn = () => apiGet(queryPath).then((res) => res.data)
  const { data, error, isFetching } = useQuery({
    queryKey: ['documentsAndSettings', candidateId],
    queryFn,
    enabled: !!candidateId,
  })

  if (error) {
    console.error('Error retrieving documents and settings:', error)
    return null
  }

  const { candidateSettings = {}, candidateDocuments = [] } = data || {}
  return {
    candidateSettings,
    candidateDocuments,
    isFetching,
  }
}
const SettingsSection = () => {
  const { candidateSettings } = useDocumentsAndSettings()
  return (
    <div className="overflow-x-auto rounded-2xl border border-silver bg-white">
      <Table className="min-w-full" tableClass="min-w-full">
        <TableHeader>
          <TableRow className="bg-intoo-blue-medium text-lg text-white hover:bg-intoo-blue-medium">
            <TableHead className="font-medium text-white">
              Transition Goal
            </TableHead>
            <TableHead className="font-medium text-white">
              Desired Job Title
            </TableHead>
            <TableHead className="font-medium text-white">
              Desired Industry
            </TableHead>
            <TableHead className="font-medium text-white">
              Desired Location
            </TableHead>
            <TableHead className="font-medium text-white">
              Experience Level or Career Level
            </TableHead>
            <TableHead className="font-medium text-white">Prior Role</TableHead>
            <TableHead className="font-medium text-white">
              Social Media Profiles
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]">
            <TableCell>{candidateSettings.transitionGoal || 'N/A'}</TableCell>
            <TableCell>{candidateSettings.desiredJobTitle || 'N/A'}</TableCell>
            <TableCell>{candidateSettings.desiredIndustry || 'N/A'}</TableCell>
            <TableCell>{candidateSettings.desiredLocation || 'N/A'}</TableCell>
            <TableCell>{candidateSettings.careerLevel || 'N/A'}</TableCell>
            <TableCell>{candidateSettings.priorRole || 'N/A'}</TableCell>
            <TableCell>
              {Array.isArray(candidateSettings.socialMediaProfiles) &&
              candidateSettings.socialMediaProfiles.length > 0
                ? candidateSettings.socialMediaProfiles.map((profile, idx) => (
                    <div key={idx}>
                      <a
                        href={profile}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-intoo-blue-dark underline"
                      >
                        {profile}
                      </a>
                    </div>
                  ))
                : 'N/A'}
            </TableCell>
          </TableRow>
        </TableBody>
        <TableHeader>
          <TableRow className="bg-intoo-blue-medium text-lg text-white hover:bg-intoo-blue-medium">
            <TableHead colSpan={7} className="font-medium text-white">
              Tell us about yourself, including your goals, what you are seeking
              from your next role, motivations, and more
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]">
            <TableCell colSpan={7}>
              {candidateSettings.aboutYourself || 'N/A'}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  )
}

const DocumentsSection = () => {
  const { candidateDocuments } = useDocumentsAndSettings()
  const formatTime = useFormatTime()

  if (!candidateDocuments || Object.keys(candidateDocuments).length === 0)
    return null

  return (
    <div className="overflow-x-auto rounded-2xl border border-silver bg-white">
      {Object.entries(candidateDocuments).map(([docType, docs]) =>
        Array.isArray(docs) && (docs.length > 0 || docType === 'resumes') ? (
          <Table
            className="mb-6 min-w-full"
            tableClass="min-w-full"
            key={docType}
          >
            <TableHeader>
              <TableRow className="bg-intoo-blue-medium text-lg text-white hover:bg-intoo-blue-medium">
                <TableHead className="font-medium capitalize text-white">
                  {docType
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase())}
                </TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map((doc, _idx) => (
                <TableRow
                  className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]"
                  key={`${doc?.className}-${doc?.id}`}
                >
                  <TableCell className="!w-full">
                    <a
                      href={doc?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {doc?.name}
                    </a>
                  </TableCell>
                  <TableCell>
                    {formatTime(moment(doc?.created_at), 'short')}
                  </TableCell>
                </TableRow>
              ))}
              {docType === 'resumes' && (
                <TableRow className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]">
                  <TableCell colSpan={2}>
                    <NewResumeModal />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        ) : null,
      )}
    </div>
  )
}

const SettingsAndDocumentsSection = () => {
  return (
    <div className="mb-8 space-y-8">
      <SettingsSection />
      <DocumentsSection />
    </div>
  )
}

export default SettingsAndDocumentsSection
